/**
 * Club & payment details for the footer — compact, dark-surface layout.
 * Single source of truth for the club's legal + bank data (moved out of the
 * About page so it is visible on every page).
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Building2, Landmark } from 'lucide-react';
import { toast } from 'sonner';

export const IBAN_VALUE = 'GR0601710400006040101437233';

const Row = ({ label, value, mono = false, testId }) => (
  <div className="flex gap-2 leading-snug">
    <dt className="text-gray-500 min-w-[92px] shrink-0">{label}</dt>
    <dd className={`text-gray-200 ${mono ? 'font-mono' : 'font-medium'}`} data-testid={testId}>{value}</dd>
  </div>
);

const ClubPaymentDetails = () => {
  const { i18n } = useTranslation();
  const isGr = i18n.language === 'gr';
  const [copied, setCopied] = useState(false);
  const clubName = isGr
    ? 'Ελληνικός Ορειβατικός Σύλλογος Ηλιούπολης'
    : 'Hellenic Mountaineering Club of Ilioupoli';

  const copyIban = async () => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(IBAN_VALUE);
        ok = true;
      }
    } catch { /* fall through to the legacy path */ }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = IBAN_VALUE;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { /* still failed */ }
    }
    if (ok) {
      setCopied(true);
      toast.success(isGr ? 'Το IBAN αντιγράφηκε' : 'IBAN copied');
      setTimeout(() => setCopied(false), 1800);
    } else {
      toast.error(isGr ? 'Αποτυχία αντιγραφής' : 'Copy failed');
    }
  };

  return (
    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 text-sm" data-testid="footer-club-payment">
      {/* Legal / tax details */}
      <div>
        <div className="flex items-center gap-2 mb-2.5">
          <Building2 className="w-4 h-4 text-gray-400 shrink-0" />
          <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
            {isGr ? 'Σύλλογος' : 'Organization'}
          </span>
        </div>
        <div className="text-gray-100 font-semibold leading-snug mb-2.5" data-testid="footer-club-name">
          {clubName}
        </div>
        <dl className="space-y-1.5">
          <Row label={isGr ? 'Επάγγελμα' : 'Legal status'}
            value={isGr ? 'Αθλητικός Σύλλογος' : 'Athletic Association'} testId="footer-club-legal" />
          <Row label={isGr ? 'ΑΦΜ' : 'Tax ID'} value="090206545" mono testId="footer-club-vat" />
          <Row label={isGr ? 'ΔΟΥ' : 'Tax office'}
            value={isGr ? 'Ηλιούπολης' : 'Ilioupoli'} testId="footer-club-taxoffice" />
        </dl>
      </div>

      {/* Bank details */}
      <div>
        <div className="flex items-center gap-2 mb-2.5">
          <Landmark className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
            {isGr ? 'Τράπεζα' : 'Bank'}
          </span>
        </div>
        <div className="text-gray-100 font-semibold leading-snug mb-2.5" data-testid="footer-bank-name">
          {isGr ? 'Τράπεζα Πειραιώς' : 'Piraeus Bank'}
        </div>
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-gray-500 min-w-[92px] shrink-0">IBAN</span>
            <span className="font-mono text-gray-100 tracking-tight break-all" data-testid="footer-iban">
              {IBAN_VALUE}
            </span>
            <button
              type="button"
              onClick={copyIban}
              className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400 hover:text-emerald-300 px-1.5 py-0.5 rounded hover:bg-white/5 transition-colors"
              aria-label={isGr ? 'Αντιγραφή IBAN' : 'Copy IBAN'}
              data-testid="footer-copy-iban"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? (isGr ? 'Αντιγράφηκε' : 'Copied') : (isGr ? 'Αντιγραφή' : 'Copy')}
            </button>
          </div>
          <Row label={isGr ? 'Δικαιούχος' : 'Beneficiary'} value={clubName} testId="footer-beneficiary" />
        </div>
      </div>
    </div>
  );
};

export default ClubPaymentDetails;
